import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { adminApi } from '../lib/adminApi';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useToast } from '../hooks/use-toast';
import {
  FileText, Plus, Pencil, Trash2, Eye, ExternalLink, Save, Search, X, Sparkles, Wand2,
} from 'lucide-react';

const blank = { title: '', slug: '', content: '', status: 'published', show_in_footer: true };

const AdminPagesPanel = () => {
  const { toast } = useToast();
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState('');
  const [editing, setEditing] = useState(null); // {originalSlug, ...form}
  const [saving, setSaving] = useState(false);
  const [preview, setPreview] = useState(false);
  const [aiBusy, setAiBusy] = useState(false);

  const aiGenerate = async () => {
    if (!editing?.title?.trim()) return toast({ title: 'Set a title first', description: 'AI needs a page title to generate content.', variant: 'destructive' });
    setAiBusy(true);
    try {
      const { content } = await adminApi.aiCmsGenerate(editing.title.trim(), '');
      if (content) setEditing((prev) => ({ ...prev, content }));
      toast({ title: 'Content generated', description: 'Review the draft and save when ready.' });
    } catch (e) {
      toast({ title: 'AI generate failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setAiBusy(false); }
  };

  const aiRewrite = async () => {
    if (!editing?.content?.trim()) return toast({ title: 'Nothing to rewrite', description: 'Add some content first.', variant: 'destructive' });
    const tone = window.prompt('Target tone (professional, friendly, concise, formal, marketing)?', 'professional');
    if (!tone) return;
    setAiBusy(true);
    try {
      const { content } = await adminApi.aiCmsRewrite(editing.content, tone, '');
      if (content) setEditing((prev) => ({ ...prev, content }));
      toast({ title: 'Content rewritten' });
    } catch (e) {
      toast({ title: 'AI rewrite failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setAiBusy(false); }
  };

  const load = async () => {
    setLoading(true);
    try {
      const data = await adminApi.listPages();
      setPages(data || []);
    } catch (e) {
      toast({ title: 'Failed to load pages', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => setEditing({ originalSlug: null, ...blank });
  const startEdit = (p) => setEditing({ originalSlug: p.slug, title: p.title, slug: p.slug, content: p.content || '', status: p.status, show_in_footer: !!p.show_in_footer });

  const save = async (e) => {
    e?.preventDefault?.();
    if (!editing?.title.trim()) return toast({ title: 'Title required', variant: 'destructive' });
    setSaving(true);
    try {
      const payload = {
        title: editing.title.trim(),
        slug: editing.slug?.trim() || '',
        content: editing.content || '',
        status: editing.status || 'draft',
        show_in_footer: !!editing.show_in_footer,
      };
      if (editing.originalSlug) {
        await adminApi.updatePage(editing.originalSlug, payload);
        toast({ title: 'Page updated' });
      } else {
        await adminApi.createPage(payload);
        toast({ title: 'Page created' });
      }
      setEditing(null);
      load();
    } catch (e) {
      toast({ title: 'Save failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    } finally { setSaving(false); }
  };

  const del = async (p) => {
    if (!window.confirm(`Delete "${p.title}"? This cannot be undone.`)) return;
    try {
      await adminApi.deletePage(p.slug);
      toast({ title: 'Page deleted' });
      load();
    } catch (e) {
      toast({ title: 'Delete failed', description: e?.response?.data?.detail || e.message, variant: 'destructive' });
    }
  };

  const filtered = (pages || []).filter((p) =>
    !q || (p.title + p.slug).toLowerCase().includes(q.toLowerCase())
  );

  return (
    <div data-testid="admin-pages-panel">
      <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <FileText className="w-5 h-5 text-[#ee5e23]" /> Pages
          </h1>
          <p className="text-sm text-gray-600">Manage your static content pages (About Us, Privacy, etc.) and create custom ones.</p>
        </div>
        <Button onClick={startNew} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="admin-pages-new-btn">
          <Plus className="w-4 h-4 mr-1" /> New Page
        </Button>
      </div>

      <div className="bg-white border border-gray-200 rounded-lg">
        <div className="p-3 border-b border-gray-100 flex items-center gap-2">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search title or slug..." className="pl-8" data-testid="admin-pages-search" />
          </div>
          <span className="text-xs text-gray-500 ml-auto">{filtered.length} of {pages.length} pages</span>
        </div>

        {loading ? (
          <div className="p-8 text-center text-gray-500">Loading...</div>
        ) : filtered.length === 0 ? (
          <div className="p-10 text-center text-gray-500">No pages yet. Click &ldquo;New Page&rdquo; to add one.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b text-xs uppercase text-gray-500">
              <tr>
                <th className="text-left px-4 py-2">Title</th>
                <th className="text-left px-4 py-2">Slug</th>
                <th className="text-left px-4 py-2">Status</th>
                <th className="text-left px-4 py-2">Footer</th>
                <th className="text-left px-4 py-2">Updated</th>
                <th className="text-right px-4 py-2">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((p) => (
                <tr key={p.id} className="border-t border-gray-100 hover:bg-orange-50/40" data-testid={`admin-page-row-${p.slug}`}>
                  <td className="px-4 py-3 font-medium text-gray-900">{p.title}</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600">/p/{p.slug}</td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${p.status === 'published' ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-200 text-gray-600'}`}>
                      {p.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs">{p.show_in_footer ? '✓' : '—'}</td>
                  <td className="px-4 py-3 text-xs text-gray-500">{new Date(p.updated_at).toLocaleDateString()}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1 justify-end">
                      <a href={`/p/${p.slug}`} target="_blank" rel="noreferrer"
                         className="p-1.5 hover:bg-gray-100 rounded text-gray-600" title="View page" data-testid={`admin-page-view-${p.slug}`}>
                        <ExternalLink className="w-4 h-4" />
                      </a>
                      <button onClick={() => startEdit(p)}
                        className="p-1.5 hover:bg-gray-100 rounded text-blue-600" title="Edit" data-testid={`admin-page-edit-${p.slug}`}>
                        <Pencil className="w-4 h-4" />
                      </button>
                      <button onClick={() => del(p)}
                        className="p-1.5 hover:bg-gray-100 rounded text-red-600" title="Delete" data-testid={`admin-page-delete-${p.slug}`}>
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing?.originalSlug ? `Edit: ${editing.originalSlug}` : 'Create new page'}</DialogTitle>
          </DialogHeader>
          {editing && (
            <form onSubmit={save} className="space-y-4 pt-2">
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cms-title" className="text-xs">Title *</Label>
                  <Input id="cms-title" value={editing.title}
                    onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                    data-testid="cms-title-input" />
                </div>
                <div>
                  <Label htmlFor="cms-slug" className="text-xs">URL slug</Label>
                  <Input id="cms-slug" value={editing.slug}
                    onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                    placeholder="auto-generated from title"
                    data-testid="cms-slug-input" />
                  <p className="text-[10px] text-gray-500 mt-1">Will be accessible at /p/{editing.slug || '(auto)'} </p>
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <div>
                  <Label htmlFor="cms-status" className="text-xs">Status</Label>
                  <select id="cms-status" value={editing.status}
                    onChange={(e) => setEditing({ ...editing, status: e.target.value })}
                    className="w-full border rounded-md px-3 py-2 text-sm"
                    data-testid="cms-status-select">
                    <option value="published">Published</option>
                    <option value="draft">Draft (hidden from public)</option>
                  </select>
                </div>
                <label className="flex items-center gap-2 text-sm mt-6">
                  <input type="checkbox" checked={editing.show_in_footer}
                    onChange={(e) => setEditing({ ...editing, show_in_footer: e.target.checked })}
                    data-testid="cms-footer-checkbox" />
                  <span>Show link in site footer</span>
                </label>
              </div>

              <div>
                <div className="flex items-center justify-between">
                  <Label htmlFor="cms-content" className="text-xs">Content (Markdown supported)</Label>
                  <div className="flex items-center gap-3">
                    <button type="button" onClick={aiGenerate} disabled={aiBusy}
                      className="text-xs text-[#ee5e23] hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="cms-ai-generate">
                      <Sparkles className="w-3.5 h-3.5" /> {aiBusy ? 'Working…' : 'Generate with AI'}
                    </button>
                    <button type="button" onClick={aiRewrite} disabled={aiBusy}
                      className="text-xs text-[#2c54b2] hover:underline inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                      data-testid="cms-ai-rewrite">
                      <Wand2 className="w-3.5 h-3.5" /> Rewrite tone
                    </button>
                    <button type="button" onClick={() => setPreview((p) => !p)}
                      className="text-xs text-[#2c54b2] hover:underline inline-flex items-center gap-1"
                      data-testid="cms-toggle-preview">
                      <Eye className="w-3.5 h-3.5" /> {preview ? 'Edit' : 'Preview'}
                    </button>
                  </div>
                </div>
                {preview ? (
                  <div className="border rounded-md p-4 bg-gray-50 min-h-[280px] prose prose-sm max-w-none">
                    <ReactMarkdown>{editing.content || '_Nothing to preview yet._'}</ReactMarkdown>
                  </div>
                ) : (
                  <Textarea id="cms-content" rows={14}
                    value={editing.content}
                    onChange={(e) => setEditing({ ...editing, content: e.target.value })}
                    placeholder="# Heading&#10;&#10;Write your content here. **Markdown** supported."
                    className="font-mono text-sm"
                    data-testid="cms-content-input" />
                )}
                <p className="text-[10px] text-gray-500 mt-1">
                  Tips: # for headings, **bold**, *italic*, [link text](https://...), - for lists, --- for divider.
                </p>
              </div>

              <div className="flex justify-end gap-2 pt-2 border-t">
                <Button type="button" variant="outline" onClick={() => setEditing(null)} data-testid="cms-cancel-btn">
                  <X className="w-4 h-4 mr-1" /> Cancel
                </Button>
                <Button type="submit" disabled={saving} className="bg-[#ee5e23] hover:bg-[#d24e15] text-white" data-testid="cms-save-btn">
                  <Save className="w-4 h-4 mr-2" />{saving ? 'Saving...' : 'Save'}
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminPagesPanel;
